<!-- VehicleFuelModal.vue -->
<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-lg bg-gray-800 text-white rounded-lg p-5 flex flex-col gap-4 relative shadow-xl animate-scaleIn">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-3">
        <div class="flex items-center gap-3">
          <img
            :src="data?.img || defaultImg"
            alt="Vehicle Image"
            class="w-12 h-12 sm:w-16 sm:h-16 rounded-md object-cover border-2"
            :class="data?.rarityColors?.borderColor || 'border-indigo-400'"
            @error="handleImageError"
          />
          <div>
            <h1 class="text-lg sm:text-xl font-semibold" :class="data?.rarityColors?.textColor || 'text-indigo-200'">
              {{ data?.name || 'ไม่ระบุ' }}
            </h1>
            <p class="text-indigo-200 text-xs sm:text-sm">ประเภท: พาหนะ</p>
            <div v-if="data?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(data.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>

        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 group"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Notification Message -->
      <div
        v-if="notificationMessage"
        class="text-center text-sm"
        :class="notificationType === 'success' ? 'text-green-400' : 'text-red-400'"
      >
        {{ notificationMessage }}
      </div>

      <!-- Content -->
      <div class="max-h-96 overflow-y-auto scrollbar-thin px-2">
        <!-- Fuel Status Section -->
        <section>
          <h2 class="text-sm font-semibold text-indigo-200 mb-2">น้ำมัน</h2>
          <div class="relative bg-gray-700 rounded-md p-3">
            <div class="bg-gray-600 h-5 rounded overflow-hidden mb-2">
              <div
                class="h-5 rounded transition-all duration-200"
                :class="fuelPercentage > 50 ? 'bg-green-500' : fuelPercentage > 20 ? 'bg-yellow-500' : 'bg-red-500'"
                :style="{ width: `${fuelPercentage}%` }"
              ></div>
            </div>

            <p class="text-sm text-center text-indigo-200">
              น้ำมัน: {{ currentFuel }} / {{ data.maxFuelLevel || 100 }}
            </p>
          </div>
        </section>

        <!-- Refuel Section -->
        <section class="mt-4">
          <div v-if="numberOfFuel === 0" class="text-center text-yellow-300 py-4">
            ไม่มีถังน้ำมันในกระเป๋า
          </div>

          <div v-else class="space-y-4">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">จำนวนถังน้ำมันที่มี</h2>

            <div class="bg-gray-700 p-3 rounded-md flex items-center justify-center gap-2">
              <i class="fas fa-gas-pump text-yellow-400 text-lg"></i>
              <p class="text-sm text-indigo-200 font-bold">{{ numberOfFuel }} ถัง</p>
            </div>

            <p class="text-sm text-yellow-300 mt-2 text-center">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              การเติมแต่ละครั้งใช้ 1 ถัง และสุ่มเพิ่มน้ำมัน 30-100 ลิตร
            </p>

            <button
              @click="refuelVehicle"
              class="w-full bg-yellow-600 hover:bg-yellow-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="isRefueling"
            >
              <span v-if="!isRefueling">
                <i class="fas fa-gas-pump mr-1 text-xs"></i>
                เติมน้ำมัน
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { projectFunctions } from '@/firebase/config'
import getUser from '@/composables/getUser'
import { usePlayerStore } from '@/composables/usePlayerStore'
import gameItemsData from '@/data/gameItems.json'
import defaultImg from '@/assets/default-item.png'

export default {
  name: 'VehicleFuelModal',

  props: {
    itemId: {
      type: String,
      required: true
    },
    inventoryItems: {
      type: Array,
      required: true
    },
    item: {
      type: Object,
      default: null
    }
  },

  emits: ['close', 'vehicle-updated'],

  setup (props, { emit }) {
    const { user } = getUser()
    const playerStore = usePlayerStore()

    const isRefueling = ref(false)
    const notificationMessage = ref('')
    const notificationType = ref('success')
    const data = ref({})

    const handleImageError = (event) => {
      event.target.src = defaultImg
    }

    const normalizeVehicle = (vehicle) => {
      if (!vehicle) return {}

      const templateId = vehicle.templateId || vehicle.id
      const template = gameItemsData[templateId] || {}

      const states = vehicle.states || {}

      return {
        ...template,
        ...vehicle,
        states,
        fuelLevel: states.fuelLevel ?? vehicle.fuelLevel ?? template.fuelLevel ?? 0,
        maxFuelLevel: states.maxFuelLevel ?? vehicle.maxFuelLevel ?? template.maxFuelLevel ?? 100
      }
    }

    const setBagVehicleData = () => {
      const bagVehicle =
        props.item ||
        props.inventoryItems.find((item) => {
          return (
            item.instanceId === props.itemId ||
            item.id === props.itemId
          )
        })

      data.value = normalizeVehicle(bagVehicle)
    }

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message
      notificationType.value = type

      setTimeout(() => {
        notificationMessage.value = ''
      }, 3000)
    }

    const currentFuel = computed(() => {
      return data.value.fuelLevel || 0
    })

    const fuelPercentage = computed(() => {
      const maxFuel = data.value.maxFuelLevel || 100
      const percent = (currentFuel.value / maxFuel) * 100

      return Math.max(0, Math.min(100, percent || 0))
    })

    const numberOfFuel = computed(() => {
      return props.inventoryItems.reduce((sum, item) => {
        return item.name === 'ถังน้ำมัน' ? sum + 1 : sum
      }, 0)
    })

    watch(
      () => playerStore.characterPrivateData,
      (charData) => {
        if (!charData) {
          showNotification('ไม่พบข้อมูลตัวละคร', 'error')
          emit('close')
          return
        }

        if (charData.vehicle === props.itemId && charData.vehicleData) {
          data.value = normalizeVehicle(charData.vehicleData)
          return
        }

        setBagVehicleData()
      },
      { immediate: true }
    )

    const refuelVehicle = async () => {
      if (numberOfFuel.value <= 0) {
        showNotification('ไม่มีถังน้ำมันในกระเป๋า', 'error')
        return
      }

      const fuelItem = props.inventoryItems.find((item) => item.name === 'ถังน้ำมัน')

      if (!fuelItem) {
        showNotification('ไม่พบถังน้ำมันในกระเป๋า', 'error')
        return
      }

      if (!fuelItem.instanceId) {
        showNotification('ถังน้ำมันไม่มี instanceId', 'error')
        return
      }

      if (!user.value?.uid) {
        showNotification('ไม่พบข้อมูลผู้ใช้', 'error')
        return
      }

      isRefueling.value = true

      try {
        const gameActions = projectFunctions.httpsCallable('gameActions')

        const result = await gameActions({
          action: 'vehicleAction',
          subAction: 'refuel',
          instanceId: props.itemId,
          fuelItemId: fuelItem.instanceId
        })

        const added = result.data?.added ?? result.data?.fuelAdded ?? 0
        const fuelLevel = result.data?.newFuelLevel ?? result.data?.fuelLevel ?? currentFuel.value

        data.value = {
          ...data.value,
          fuelLevel,
          states: {
            ...(data.value.states || {}),
            fuelLevel
          }
        }

        showNotification(`เติมน้ำมันสำเร็จ! +${added} ลิตร`, 'success')

        emit('vehicle-updated', {
          id: props.itemId,
          instanceId: props.itemId,
          fuelLevel
        })
      } catch (err) {
        console.error('Error refueling vehicle:', err)
        showNotification(err.message || 'เกิดข้อผิดพลาดในการเติมน้ำมัน', 'error')
      } finally {
        isRefueling.value = false
      }
    }

    onMounted(() => {
      setBagVehicleData()
    })

    return {
      isRefueling,
      data,
      currentFuel,
      fuelPercentage,
      numberOfFuel,
      handleImageError,
      refuelVehicle,
      notificationMessage,
      notificationType,
      defaultImg
    }
  }
}
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes modalFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
</style>